import { MentorPerformanceControls } from "@/components/mentor-performance/controls";
import { MentorsTable } from "@/components/mentor-performance/mentors-table";
import { SummaryStrip } from "@/components/mentor-performance/summary-strip";
import {
  DEFAULT_MENTOR_METRICS_SORT,
  ROLLING_30_DAYS_PERIOD,
  isRollingMetricSort,
  useMentorMetricsSummary,
  useMentorMetricsTable,
  useMentorMetricsTableOverlay,
  usesRolling30DayMetrics,
} from "@/lib/api/mentor-metrics-dashboard";
import { useAbility } from "@/lib/casl/ability-context";
import { RESOURCES } from "@/lib/casl/rbac";
import {
  mergeMentorMetricsSummary,
  mergeMentorMetricsTablePage,
} from "@/lib/hooks/mentor-performance/merge-rolling-metrics";
import { useMentorMetricsParams } from "@/lib/hooks/mentor-performance/use-mentor-metrics-params";

export function MentorPerformanceOverview() {
  const ability = useAbility();
  const {
    period,
    leadMentorIds,
    search,
    sort,
    page,
    pageSize,
    pageSizes,
    setPeriod,
    setLeadMentorIds,
    setSearch,
    setSort,
    setPage,
    setPageSize,
  } = useMentorMetricsParams();

  const canFilterLeadMentors = ability.can(
    "read",
    RESOURCES.MENTOR_METRICS_DASHBOARD_LEAD_FILTER,
  );
  const scopedLeadMentorIds = canFilterLeadMentors ? leadMentorIds : [];
  const useRollingWindow = usesRolling30DayMetrics(period);
  const monthScope = { period, leadMentorIds: scopedLeadMentorIds };
  const rollingScope = {
    period: ROLLING_30_DAYS_PERIOD,
    leadMentorIds: scopedLeadMentorIds,
  };
  const sortUsesRollingWindow = useRollingWindow && isRollingMetricSort(sort);
  const primaryScope = sortUsesRollingWindow ? rollingScope : monthScope;
  const overlayScope = sortUsesRollingWindow ? monthScope : rollingScope;

  const monthSummaryQuery = useMentorMetricsSummary(monthScope);
  const rollingSummaryQuery = useMentorMetricsSummary(
    rollingScope,
    useRollingWindow,
  );
  const tableQuery = useMentorMetricsTable({
    ...primaryScope,
    search,
    sort,
    page,
    pageSize,
  });
  const overlayQuery = useMentorMetricsTableOverlay(
    {
      ...overlayScope,
      search,
      sort: DEFAULT_MENTOR_METRICS_SORT,
    },
    useRollingWindow,
  );

  const summary = useRollingWindow
    ? monthSummaryQuery.data && rollingSummaryQuery.data
      ? mergeMentorMetricsSummary(
          monthSummaryQuery.data,
          rollingSummaryQuery.data,
        )
      : undefined
    : monthSummaryQuery.data;
  const tableData = !tableQuery.data
    ? undefined
    : useRollingWindow
      ? mergeMentorMetricsTablePage(
          tableQuery.data,
          overlayQuery.data?.rows ?? [],
          sortUsesRollingWindow ? "apply-month-caseload" : "apply-rolling",
          sortUsesRollingWindow
            ? tableQuery.data.appealScoreAvailable
            : overlayQuery.data?.appealScoreAvailable ??
                tableQuery.data.appealScoreAvailable,
        )
      : tableQuery.data;

  return (
    <div className="min-h-screen w-full bg-surfaces-z3 p-5 font-body md:p-8">
      <div className="flex w-full flex-col gap-6">
        <header className="flex min-h-[60px] flex-col justify-center">
          <h1 className="font-heading text-header-2 text-black">
            Mentor Performance Overview
          </h1>
          <p className="mt-2 text-body-1 text-text-secondary">All Mentors</p>
        </header>

        <MentorPerformanceControls
          period={period}
          availableMonths={monthSummaryQuery.data?.availableMonths ?? []}
          leadMentorIds={leadMentorIds}
          search={search}
          showLeadMentorFilter={canFilterLeadMentors}
          onPeriodChange={setPeriod}
          onLeadMentorIdsChange={setLeadMentorIds}
          onSearchChange={setSearch}
        />

        <SummaryStrip
          summary={summary}
          isLoading={
            (monthSummaryQuery.isPending || rollingSummaryQuery.isPending) &&
            !summary
          }
          isRefreshing={
            (monthSummaryQuery.isFetching || rollingSummaryQuery.isFetching) &&
            !!summary
          }
          isError={
            (monthSummaryQuery.isError || rollingSummaryQuery.isError) &&
            !summary
          }
        />

        <MentorsTable
          data={tableData}
          isLoading={tableQuery.isPending && !tableQuery.data}
          isRefreshing={
            (tableQuery.isFetching || overlayQuery.isFetching) &&
            !!tableQuery.data
          }
          isError={tableQuery.isError && !tableQuery.data}
          sort={sort}
          page={page}
          pageSize={pageSize}
          pageSizes={pageSizes}
          onSortChange={setSort}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
}
